import { Codec, VideoCodec } from '@4players/odin-common';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { isTauri } from '../helpers/platform';

const ALL_CODECS: Codec[] = ['H264', 'VP8', 'VP9', 'AV1'];
// The native app encodes in the plugin, which offers H.264 only for now
// (see `nativeCaptureConfig`); the WebView's WebCodecs support is irrelevant.
export const VALID_CODECS: Codec[] = isTauri
  ? ['H264']
  : ALL_CODECS.filter((c) => new VideoCodec(c).isSupported());

export const H264_MAX_RESOLUTION = { width: 4096, height: 2304 };

// All presets are 16:9 to match the video tiles. '480p' used to be 640×480;
// saved overrides are migrated in media-config.repository.
export const VALID_RESOLUTIONS = [
  { label: '480p', value: { width: 854, height: 480 } },
  { label: '720p', value: { width: 1280, height: 720 } },
  { label: '1080p', value: { width: 1920, height: 1080 } },
  { label: _('Source'), value: { width: 0, height: 0 } },
] as const;
type Resolution = (typeof VALID_RESOLUTIONS)[number]['value'];

export const VALID_BITRATES = [500, 1000, 1500, 3000, 8000, 15000, 30000] as const;
type Bitrate = (typeof VALID_BITRATES)[number];

export const VALID_FRAMERATES = [5, 15, 30, 60] as const;
type Framerate = (typeof VALID_FRAMERATES)[number];

export const VALID_DEGRADATION_MODES = [
  { label: _('Balanced'), value: 'balanced' },
  { label: _('FPS'), value: 'maintain-framerate' },
  { label: _('Resolution'), value: 'maintain-resolution' },
] as const;
type DegradationMode = (typeof VALID_DEGRADATION_MODES)[number]['value'];

export interface VideoSettings {
  codec: Codec;
  resolution: Resolution;
  maxBitrate: Bitrate;
  maxFramerate: Framerate;
  degradationPreference: DegradationMode;
}

export const DEFAULT_CAMERA_SETTINGS: VideoSettings = {
  codec: 'H264',
  resolution: VALID_RESOLUTIONS[0].value, // 480p
  maxBitrate: 1000,
  maxFramerate: 15,
  degradationPreference: 'maintain-framerate',
};

export const DEFAULT_SCREEN_SETTINGS: VideoSettings = {
  codec: 'H264',
  resolution: VALID_RESOLUTIONS[2].value, // 1080p
  maxBitrate: 3000,
  maxFramerate: 15,
  degradationPreference: 'maintain-resolution',
};
